<template>
  <div class="preview-wrapper">
    <div class="preview-container">
      <div class="preview-placeholder" v-if="!uploadedImage">
        <i class="fas fa-images"></i>
        <p>{{ $t('manga.preview.placeholder') }}</p>
      </div>
      <img v-else :src="uploadedImage" :alt="$t('translate.imagePreview')" class="preview-img">
    </div>

    <div class="thumbnails" v-if="props.uploadedImages && props.uploadedImages.length > 0">
      <img v-for="(img, index) in props.uploadedImages" :key="index" :src="img" class="thumbnail"
        :class="{ active: index === props.currentIndex }" @click="selectImage(index)">
    </div>

    <div class="preview-nav" v-if="props.uploadedImages && props.uploadedImages.length > 0">
      <el-button class="preview-btn" @click="prevImage" :disabled="props.currentIndex <= 0" size="small">
        <i class="fas fa-chevron-left"></i>
      </el-button>
      <div class="preview-info">{{ $t('translate.imageCount', { current: props.currentIndex + 1, total: props.uploadedImages.length, size: totalSizeText }) }}</div>
      <el-button class="preview-btn" @click="nextImage" :disabled="props.currentIndex >= props.uploadedImages.length - 1"
        size="small">
        <i class="fas fa-chevron-right"></i>
      </el-button>
      <el-button class="preview-clear" @click="clearAllImages" size="small" type="primary" plain>
        {{ $t('manga.preview.clearAll') }}
      </el-button>
    </div>

    <div class="upload-progress-section" v-if="localLoading">
      <div class="upload-progress-bar">
        <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <div class="upload-progress-text">{{ $t('manga.preview.uploading', { progress: uploadProgress }) }}</div>
    </div>
    <el-button class="start-translate-btn" :disabled="!canTranslate" @click="startTranslation" type="primary"
      size="large" :loading="isTranslating || localLoading">
      <i class="fas fa-language" style="margin-right: 8px;"></i> {{ $t('manga.preview.joinQueue') }}
    </el-button>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { batchMangaUpload } from '@/api/manga/manga.js'

const { t } = useI18n()

const props = defineProps({
  uploadedImage: {
    type: String,
    default: ''
  },
  tempFile: {
    type: Object,
    default: null
  },
  queueCount: {
    type: Number,
    default: 0
  },
  isTranslating: {
    type: Boolean,
    default: false
  },
  onFileAdded: {
    type: Function,
    default: () => { }
  },
  onTranslationStatusChange: {
    type: Function,
    default: () => { }
  },
  uploadedImages: {
    type: Array,
    default: () => []
  },
  currentIndex: {
    type: Number,
    default: 0
  },
  allFiles: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['file-change', 'clear-all'])

const localLoading = ref(false)
const uploadProgress = ref(0)

const currentImageUrl = computed(() => {
  if (props.uploadedImages.length > 0 && props.currentIndex < props.uploadedImages.length) {
    return props.uploadedImages[props.currentIndex]
  }
  return props.uploadedImage
})

const canTranslate = computed(() => {
  return props.uploadedImages.length > 0 && !props.isTranslating && !localLoading.value
})

const totalSizeText = computed(() => {
  if (!props.allFiles || props.allFiles.length === 0) return '0MB'
  const bytes = props.allFiles.reduce((sum, f) => sum + (f.size || 0), 0)
  const mb = bytes / 1024 / 1024
  return mb >= 1 ? mb.toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB'
})

const selectImage = (index) => {
  if (index >= 0 && index < props.uploadedImages.length) {
    emit('select-image', {
      index: index,
      imageUrl: props.uploadedImages[index]
    })
  }
}

const prevImage = () => {
  if (props.currentIndex > 0) {
    selectImage(props.currentIndex - 1)
  }
}

const nextImage = () => {
  if (props.currentIndex < props.uploadedImages.length - 1) {
    selectImage(props.currentIndex + 1)
  }
}

const clearAllImages = () => {
  if (props.uploadedImages.length > 0) {
    emit('clear-all')
  }
}

const startTranslation = async () => {
  if (!canTranslate.value || !props.onFileAdded) {
    return
  }

  if (props.isTranslating || props.uploadedImages.length === 0 || localLoading.value) {
    return
  }

  localLoading.value = true
  props.onTranslationStatusChange(true)

  try {
    const filesToUpload = getFilesToUpload()

    if (filesToUpload.length === 0) {
      ElMessage.warning(t('translate.uploadFirst'))
      localLoading.value = false
      uploadProgress.value = 0
      props.onTranslationStatusChange(false)
      return
    }

    await uploadMultipleFiles(filesToUpload)
  } catch (error) {
    console.error('upload failed:', error)
    ElMessage.error(`${t('translate.uploadFailed')}：` + (error.message || t('translate.uploadNetworkError')))
    localLoading.value = false
    uploadProgress.value = 0
    props.onTranslationStatusChange(false)
  }
}

const getFilesToUpload = () => {
  if (props.allFiles && props.allFiles.length > 0) {
    return props.allFiles
  }
  if (props.tempFile) {
    return [props.tempFile]
  }
  return []
}

const uploadMultipleFiles = async (files) => {
  let slowTimer = null
  try {
    uploadProgress.value = 0
    const resultInfo = await batchMangaUpload(files, (percent) => {
      const capped = Math.min(percent, 90)
      if (capped >= 90 && !slowTimer) {
        let current = 90
        slowTimer = setInterval(() => {
          current++
          if (current >= 99) {
            clearInterval(slowTimer)
            slowTimer = null
            return
          }
          uploadProgress.value = current
        }, 555)
      }
      if (capped < 90) {
        uploadProgress.value = capped
      }
    })

    if (slowTimer) { clearInterval(slowTimer); slowTimer = null }
    uploadProgress.value = 100

    let successCount = 0
    let failCount = 0
    const errorMessages = []

    const batchResult = resultInfo?.data || {}
    const uploadedFiles = batchResult.uploadedFiles || []
    for (const file of uploadedFiles) {
      successCount++
      props.onFileAdded({
        id: file.img_id,
        fileName: file.originalName,
        status: '翻译中',
        uploadTime: new Date().toLocaleTimeString(),
        imgId: file.img_id,
      })
    }
    const errors = batchResult.errors || []
    failCount = errors.length
    errorMessages.push(...errors)

    if (successCount > 0) {
      clearAllImages()
      localLoading.value = false
      uploadProgress.value = 0
      props.onTranslationStatusChange(false)

      if (errorMessages.length > 0) {
        const html = `<div class="upload-error-list">${errorMessages.map(m => `<div class="upload-error-item">${m}</div>`).join('')}</div>`
        ElMessageBox.alert(html, t('translate.uploadResult'), {
          confirmButtonText: t('translate.confirm'),
          type: 'warning',
          dangerouslyUseHTMLString: true,
          customClass: 'batch-upload-result-dialog',
          customStyle: { 'max-width': '600px', 'width': '85%' }
        })
      } else {
        ElMessage.success(t('translate.uploadSuccess', { count: successCount }))
      }
    } else {
      localLoading.value = false
      uploadProgress.value = 0
      props.onTranslationStatusChange(false)
      const html = `<div class="upload-error-list">${errorMessages.map(m => `<div class="upload-error-item">${m}</div>`).join('')}</div>`
      ElMessageBox.alert(html, t('translate.uploadFailed'), {
        confirmButtonText: t('translate.confirm'),
        type: 'error',
        dangerouslyUseHTMLString: true,
        customClass: 'batch-upload-result-dialog',
        customStyle: { 'max-width': '600px', 'width': '85%' }
      })
    }
  } catch (error) {
    if (slowTimer) { clearInterval(slowTimer); slowTimer = null }
    console.error('batch upload failed:', error)
    ElMessage.error(`${t('translate.uploadFailed')}：` + (error.message || t('translate.uploadNetworkError')))
    localLoading.value = false
    uploadProgress.value = 0
    props.onTranslationStatusChange(false)
  }
}
</script>

<style scoped>
.preview-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.preview-container {
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  background: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 1rem;
  border: 1px solid rgba(94, 234, 212, 0.1);
  flex: 1;
  width: 100%;
  max-width: 100%;
}

.preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.preview-placeholder {
  text-align: center;
  color: #94a3b8;
}

.preview-placeholder i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #475569;
}

.thumbnails {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 6px 2px;
  margin: 8px 0;
  max-height: 62px;
  flex-wrap: nowrap;
  width: 100%;
  max-width: 100%;
}

.thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 50px;
}

.thumbnail.active {
  border-color: #5eead4;
}

.thumbnail:hover {
  border-color: rgba(94, 234, 212, 0.5);
}

.thumbnails::-webkit-scrollbar {
  height: 4px;
}

.thumbnails::-webkit-scrollbar-track {
  background: rgba(94, 234, 212, 0.1);
  border-radius: 2px;
}

.thumbnails::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.3);
  border-radius: 2px;
}

.thumbnails::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.5);
}

.preview-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 0;
  width: 100%;
  max-width: 100%;
}

:deep(.preview-btn.el-button) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 6px !important;
  width: 36px !important;
  height: 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
}

:deep(.preview-btn.el-button:hover:not(:disabled)) {
  background: rgba(94, 234, 212, 0.2) !important;
}

:deep(.preview-btn.el-button:disabled) {
  opacity: 0.4 !important;
}

.preview-info {
  flex: 1;
  text-align: center;
  color: #cbd5e1;
  font-size: 0.85rem;
}

:deep(.preview-clear.el-button--primary.is-plain) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 6px !important;
  font-size: 0.8rem !important;
}

:deep(.preview-clear.el-button--primary.is-plain:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
}

.upload-progress-section {
  margin: 8px 0 4px;
  width: 100%;
}

.upload-progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(94, 234, 212, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #5eead4, #3b82f6);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.upload-progress-text {
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
  margin-top: 4px;
}

:deep(.start-translate-btn.el-button--primary) {
  background: linear-gradient(90deg, #5eead4, #3b82f6) !important;
  color: #0f172a !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  width: 100% !important;
  margin-top: 8px !important;
  box-shadow: 0 4px 15px rgba(94, 234, 212, 0.2) !important;
  transition: all 0.3s ease !important;
}

:deep(.start-translate-btn.el-button--primary:hover:not(:disabled)) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(94, 234, 212, 0.3) !important;
}

:deep(.start-translate-btn.el-button--primary:disabled) {
  opacity: 0.5 !important;
}

:global(.batch-upload-result-dialog) {
  background: #1e293b !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
}

:global(.batch-upload-result-dialog .el-message-box__header) {
  padding: 20px 20px 10px !important;
}

:global(.batch-upload-result-dialog .el-message-box__title) {
  color: #e2e8f0 !important;
  font-size: 1.1rem !important;
}

:global(.batch-upload-result-dialog .el-message-box__message) {
  white-space: pre-line !important;
  line-height: 1.8 !important;
  font-size: 15px !important;
  word-break: break-all;
  margin-top: 10px !important;
  color: #cbd5e1 !important;
}

:global(.batch-upload-result-dialog .el-message-box__content) {
  max-height: 500px !important;
  overflow-y: auto !important;
  padding: 20px !important;
}

:global(.batch-upload-result-dialog .el-message-box__btns) {
  padding: 10px 20px 20px !important;
}

:global(.batch-upload-result-dialog .el-message-box__btns .el-button--primary) {
  background: linear-gradient(90deg, #5eead4, #3b82f6) !important;
  color: #0f172a !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  padding: 10px 28px !important;
  box-shadow: 0 4px 15px rgba(94, 234, 212, 0.2) !important;
}

:global(.batch-upload-result-dialog .el-message-box__btns .el-button--primary:hover) {
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 20px rgba(94, 234, 212, 0.3) !important;
}

:global(.batch-upload-result-dialog .el-message-box__close) {
  display: none !important;
}

:global(.batch-upload-result-dialog .el-message-box__status) {
  display: none !important;
}

:global(.batch-upload-result-dialog .el-icon-error) {
  display: none !important;
}
</style>
